import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, Phone, MessagesSquare, Users, Bot, FileText, Megaphone, Terminal, KeyRound,
  Webhook, BarChart3, ShieldCheck, Settings, PanelLeftClose, PanelLeftOpen, LogOut, Menu, X,
} from "lucide-react";
import { Logo } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export const NAV = [
  ["/", "Overview", LayoutDashboard], ["/whatsapp-accounts", "WhatsApp Accounts", Building2], ["/phone-numbers", "Phone Numbers", Phone],
  ["/inbox", "Inbox", MessagesSquare], ["/contacts", "Contacts", Users], ["/chatbot", "AI Chatbot", Bot],
  ["/templates", "Message Templates", FileText], ["/campaigns", "Campaigns", Megaphone], ["/playground", "API Playground", Terminal],
  ["/api-keys", "API Keys", KeyRound], ["/webhooks", "Webhooks", Webhook], ["/analytics", "Analytics", BarChart3],
  ["/team", "Team & Roles", ShieldCheck], ["/settings", "Settings", Settings],
];
const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

function NavItems({ collapsed, onNavigate }) {
  return (
    <nav className="flex-1 overflow-y-auto scroll-thin px-3 py-3 space-y-0.5">
      {NAV.map(([to, label, Icon]) => (
        <NavLink key={to} to={to} end={to === "/"} onClick={onNavigate} data-testid={`nav-${slug(label)}`} title={collapsed ? label : undefined}
          className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${collapsed ? "justify-center" : ""} ${isActive ? "bg-emerald-50 text-[#075E54]" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
          {({ isActive }) => (<><Icon className={`w-[18px] h-[18px] shrink-0 ${isActive ? "text-[#128C7E]" : ""}`} />{!collapsed && <span className="truncate">{label}</span>}</>)}
        </NavLink>
      ))}
    </nav>
  );
}

function ConnectionPill() {
  const [state, setState] = useState(null);
  const { pathname } = useLocation();
  useEffect(() => {
    api.get("/whatsapp-accounts").then((r) => setState(r.data.some((a) => a.status === "connected") ? "live" : "demo")).catch(() => setState(null));
  }, [pathname]);
  if (!state) return null;
  return state === "live" ? (
    <span data-testid="meta-status-pill" className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-xs font-semibold text-emerald-700"><span className="w-1.5 h-1.5 rounded-full bg-[#25D366] animate-pulse" />Meta API connected</span>
  ) : (
    <span data-testid="meta-status-pill" className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-violet-50 border border-violet-200 text-xs font-semibold text-violet-700"><span className="w-1.5 h-1.5 rounded-full bg-violet-500" />Demo mode</span>
  );
}

export default function Layout() {
  const { session, logout } = useAuth();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(localStorage.getItem("c360_sidebar") === "1");
  const [mobile, setMobile] = useState(false);
  const toggle = () => { localStorage.setItem("c360_sidebar", collapsed ? "0" : "1"); setCollapsed(!collapsed); };
  const u = session.user;

  return (
    <div className="min-h-screen flex bg-slate-50">
      <aside className={`hidden lg:flex flex-col bg-white border-r border-slate-200 sticky top-0 h-screen transition-[width] duration-200 ${collapsed ? "w-20" : "w-64"}`} data-testid="sidebar">
        <div className={`h-16 flex items-center border-b border-slate-100 ${collapsed ? "justify-center" : "px-5"}`}><Logo collapsed={collapsed} /></div>
        <NavItems collapsed={collapsed} />
        <div className="p-3 border-t border-slate-100">
          <button onClick={toggle} data-testid="sidebar-collapse-toggle" className="w-full flex items-center justify-center gap-2 rounded-lg py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50">
            {collapsed ? <PanelLeftOpen className="w-4 h-4" /> : <><PanelLeftClose className="w-4 h-4" />Collapse</>}
          </button>
        </div>
      </aside>
      {mobile && (
        <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setMobile(false)}>
          <div className="absolute inset-0 bg-slate-900/30" />
          <aside className="absolute left-0 top-0 h-full w-72 bg-white flex flex-col fade-up" onClick={(e) => e.stopPropagation()} data-testid="mobile-sidebar">
            <div className="h-16 flex items-center justify-between px-5 border-b border-slate-100"><Logo /><button onClick={() => setMobile(false)}><X className="w-5 h-5" /></button></div>
            <NavItems onNavigate={() => setMobile(false)} />
          </aside>
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 flex items-center gap-3 px-4 sm:px-6">
          <button className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-slate-100" onClick={() => setMobile(true)} data-testid="mobile-menu-button"><Menu className="w-5 h-5" /></button>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Workspace</div>
            <div className="text-sm font-bold text-slate-800 truncate" data-testid="tenant-name">{session.tenant?.name}</div>
          </div>
          <div className="flex-1" />
          <ConnectionPill />
          <div className="flex items-center gap-2.5 pl-3 border-l border-slate-200">
            <div className="w-8 h-8 rounded-full bg-[#075E54] text-white text-xs font-bold flex items-center justify-center">{u.name?.[0]?.toUpperCase()}</div>
            <div className="hidden md:block leading-tight">
              <div className="text-sm font-semibold text-slate-800" data-testid="current-user-name">{u.name}</div>
              <div className="text-[11px] text-slate-400 capitalize" data-testid="current-user-role">{u.role}</div>
            </div>
            <button data-testid="logout-button" title="Sign out" onClick={async () => { await logout(); nav("/login"); }} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100"><LogOut className="w-4 h-4" /></button>
          </div>
        </header>
        <main className="flex-1 min-w-0"><Outlet /></main>
      </div>
    </div>
  );
}
